import React, { useRef, useState, useEffect } from "react";
import { Box, Typography, useTheme, useMediaQuery } from "@mui/material";
import useIsInView from "../../../hooks/useIsInView";

/**
 * 타이핑 효과 컴포넌트
 *
 * Props:
 * @param {Array} texts - 타이핑 할 텍스트 배열 [Optional, 기본값: ['Hello Designers', 'You can make it', 'With Cursor AI.']]
 * @param {number} typingSpeed - 타이핑 속도 (ms) [Optional, 기본값: 100]
 * @param {number} deleteSpeed - 삭제 속도 (ms) [Optional, 기본값: 50]
 * @param {string} cursorColor - 커서 색상 [Optional, 기본값: 텍스트 색상과 동일]
 * @param {string} textColor - 텍스트 색상 [Optional, 기본값: 'inherit']
 * @param {object} fontSize - 폰트 사이즈 객체 [Optional, 기본값: {}]
 * @param {object|string} variant - Typography 변형 (반응형 객체 또는 문자열) [Optional, 기본값: { xs: "h3", sm: "h3", md: "h2", lg: "h1" }]
 * @param {string} fontFamily - 폰트 패밀리 [Optional, 기본값: 'monospace']
 * @param {number} startDelay - 타이핑 시작 전 대기 시간 (ms) [Optional, 기본값: 0]
 * @param {string} cursorType - 커서 타입 ('line', 'circle', 'square') [Optional, 기본값: 'line']
 * @param {object} sx - 추가 스타일 객체 [Optional, 기본값: {}]
 * @param {string} persistKey - 세션 저장 키(여러 인스턴스 구분용) [Optional]
 * @param {boolean} respectInView - 뷰포트 감지를 엄격히 따를지 여부 [Optional, 기본값: true]
 * @param {boolean} loop - 마지막 텍스트까지 완료 후 반복 재생할지 여부 [Optional, 기본값: false]
 *
 * Example usage:
 * <TypingEffectDemo texts={['Hello', 'World', 'Typing Effect']} typingSpeed={80} variant={{ xs: "h4", md: "h2" }} cursorType="circle" />
 */
function TypingEffect({
  texts = ["Hello Designers", "You can make it", "With Cursor AI."],
  typingSpeed = 100,
  deleteSpeed = 50,
  cursorColor = "#fff",
  textColor = "inherit",
  fontSize,
  variant = { xs: "h3", sm: "h3", md: "h2", lg: "h1" },
  textAlign = "left",
  fontFamily,
  fontWeight = "bold",
  sx = {},
  startDelay = 0,
  cursorType = "line",
  persistKey,
  respectInView = true,
  loop = false,
}) {
  const textRef = useRef(null);
  const [containerRef, isInView] = useIsInView();
  const [currentText, setCurrentText] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [charIndex, setCharIndex] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const theme = useTheme();

  // 반응형 variant 처리
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  // 현재 화면 크기에 따른 variant 결정
  const getResponsiveVariant = () => {
    if (typeof variant === 'string') {
      return variant;
    }
    
    if (typeof variant === 'object') {
      if (isLg && variant.lg) return variant.lg;
      if (isMd && variant.md) return variant.md;
      if (isSm && variant.sm) return variant.sm;
      if (isXs && variant.xs) return variant.xs;
      
      // 기본값 fallback
      return variant.md || variant.sm || variant.xs || 'body1';
    }
    
    return 'body1';
  };

  const currentVariant = getResponsiveVariant();

  // texts 배열의 유효성을 확인하고 필요하면 기본값으로 설정
  const safeTexts =
    Array.isArray(texts) && texts.length > 0 && texts.some((t) => typeof t === 'string' && t.trim().length > 0)
      ? texts
      : ["Hello Designer"];

  // 상태 세션 보존/복원 키
  const storageKey = (
    persistKey
      ? `TypingEffect:${persistKey}`
      : (() => {
          try {
            const path = typeof window !== 'undefined' && window.location ? window.location.pathname : '';
            return `TypingEffect:${path}:${JSON.stringify(safeTexts)}`;
          } catch (_) {
            return 'TypingEffect:default';
          }
        })()
  );

  const didHydrateRef = useRef(false);

  // 초기 마운트 시 세션에서 상태 복원
  useEffect(() => {
    try {
      // 하드 리로드 시에는 복원하지 않고 처음부터 재생
      const isHardReload = (() => {
        try {
          const entries = typeof window !== 'undefined' && window.performance && window.performance.getEntriesByType
            ? window.performance.getEntriesByType('navigation')
            : [];
          if (entries && entries[0] && entries[0].type) {
            return entries[0].type === 'reload';
          }
          // deprecated fallback
          const nav = typeof window !== 'undefined' && window.performance ? window.performance.navigation : null;
          if (nav && typeof nav.type === 'number') {
            return nav.type === 1; // TYPE_RELOAD
          }
        } catch (_) {}
        return false;
      })();

      if (isHardReload) {
        // 리로드 시 세션 상태는 무시 (필요 시 깨끗이 비움)
        try { if (typeof window !== 'undefined') { window.sessionStorage.removeItem(storageKey); } } catch (_) {}
        return;
      }

      const raw = typeof window !== 'undefined' ? window.sessionStorage.getItem(storageKey) : null;
      if (raw) {
        const saved = JSON.parse(raw);
        const sig = JSON.stringify(safeTexts);
        if (saved && saved.sig === sig) {
          setCurrentText(saved.currentText || "");
          setTextIndex(typeof saved.textIndex === "number" ? saved.textIndex : 0);
          setCharIndex(typeof saved.charIndex === "number" ? saved.charIndex : 0);
          setIsTyping(Boolean(saved.isTyping));
          setHasStarted(Boolean(saved.hasStarted));
          didHydrateRef.current = true;
        }
      }
    } catch (_) {
      // ignore
    }
  }, []);

  // 외부 props(texts 등) 변경 시 기존 타이핑 상태가 섞이는 문제 방지: 완전 초기화
  const prevTextsSigRef = useRef('');
  useEffect(() => {
    try {
      const sig = JSON.stringify(safeTexts);
      if (sig !== prevTextsSigRef.current) {
        prevTextsSigRef.current = sig;
        if (didHydrateRef.current) {
          // 복원 직후 1회 초기화 건너뛰기
          didHydrateRef.current = false;
          return;
        }
        // 타이핑 상태 초기화
        setCurrentText("");
        setTextIndex(0);
        setCharIndex(0);
        setIsTyping(false);
        setHasStarted(false);
      }
    } catch (_) {
      if (didHydrateRef.current) {
        // 복원 직후 예외 시에도 초기화 건너뛰기
        didHydrateRef.current = false;
        return;
      }
      // 안전하게 초기화
      setCurrentText("");
      setTextIndex(0);
      setCharIndex(0);
      setIsTyping(false);
      setHasStarted(false);
    }
    // safeTexts가 바뀌면 초기화
  }, [safeTexts]);

  // 상태 변경 시 세션에 저장
  useEffect(() => {
    try {
      const payload = {
        currentText,
        textIndex,
        charIndex,
        isTyping,
        hasStarted,
        sig: JSON.stringify(safeTexts),
      };
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(storageKey, JSON.stringify(payload));
      }
    } catch (_) {}
  }, [currentText, textIndex, charIndex, isTyping, hasStarted, safeTexts]);

  // 애니메이션 단계 전환 딜레이 값
  const delayAfterTyping = 1000; // 타이핑 완료 후 삭제 시작 전까지 대기 시간
  const delayBeforeTyping = 500; // 텍스트 삭제 완료 후 다음 텍스트 타이핑 시작 전 대기 시간

  // 화면에 보일 때 애니메이션 시작
  useEffect(() => {
    // hasStarted가 false면 무조건 시작 (뷰포트 여부/복원 여부 무시)
    if (!hasStarted) {
      const startTimeout = setTimeout(() => {
        setIsTyping(true);
        setHasStarted(true);
      }, startDelay);

      return () => clearTimeout(startTimeout);
    }
  }, [hasStarted, startDelay]);

  // 타이핑 애니메이션 효과 구현
  useEffect(() => {
    // 애니메이션이 시작되지 않았거나 화면에 보이지 않으면 실행하지 않음
    const canAnimate = hasStarted && (respectInView ? isInView : true);
    if (!canAnimate) return;

    // 현재 출력할 전체 텍스트
    const currentFullText = safeTexts[textIndex] || "";

    // 현재 텍스트가 마지막 텍스트인지 확인
    const isLastText = textIndex === safeTexts.length - 1;

    // 1. 타이핑 진행 중 (글자를 하나씩 추가)
    if (isTyping && charIndex < currentFullText.length) {
      const typingTimeout = setTimeout(() => {
        setCurrentText((prev) => prev + currentFullText.charAt(charIndex));
        setCharIndex(charIndex + 1);
      }, typingSpeed);
      return () => clearTimeout(typingTimeout);
    }

    // 2. 타이핑 완료, 잠시 대기 후 다음 단계
    else if (isTyping && charIndex >= currentFullText.length) {
      const pauseTimeout = setTimeout(() => {
        // 마지막 텍스트: loop가 아니면 종료, loop면 삭제 단계로 전환
        if (isLastText && !loop) {
          return; // 애니메이션 종료 (커서만 계속 깜빡임)
        }
        // 다음 단계: 텍스트 삭제 시작
        setIsTyping(false);
      }, delayAfterTyping);
      return () => clearTimeout(pauseTimeout);
    }

    // 3. 삭제 진행 중 (글자를 하나씩 제거)
    else if (!isTyping && charIndex > 0) {
      const deletingTimeout = setTimeout(() => {
        setCurrentText((prev) => prev.slice(0, -1));
        setCharIndex(charIndex - 1);
      }, deleteSpeed);
      return () => clearTimeout(deletingTimeout);
    }

    // 4. 삭제 완료, 다음 텍스트로 이동
  else if (!isTyping && charIndex === 0) {
      // 마지막 텍스트에서 빈 상태로 복원된 경우, 안전하게 재시작
      if (isLastText) {
        if (loop) {
          const restartTimeout = setTimeout(() => {
            setTextIndex(0);
            setIsTyping(true);
          }, delayBeforeTyping);
          return () => clearTimeout(restartTimeout);
        }
        return;
      }

      const nextTextTimeout = setTimeout(() => {
        // 다음 텍스트로 이동
        setTextIndex(textIndex + 1);
        setIsTyping(true);
      }, delayBeforeTyping);
      return () => clearTimeout(nextTextTimeout);
    }
  }, [
    textIndex,
    charIndex,
    isTyping,
    safeTexts,
    typingSpeed,
    deleteSpeed,
    isInView,
    hasStarted,
    respectInView,
  ]);

  // 커서 타입별 스타일 설정
  const getCursorStyle = () => {
    // 기본 스타일
    const baseStyle = {
      backgroundColor: cursorColor || textColor,
      animation: "blink 1s ease-in-out infinite",
    };

    switch (cursorType) {
      case "circle":
        return {
          ...baseStyle,
          width: fontSize ? `${fontSize * 0.2}px` : "0.2em",
          height: fontSize ? `${fontSize * 0.2}px` : "0.2em",
          borderRadius: "50%",
          display: "inline-block",
          position: "relative",
          verticalAlign: "text-bottom",
        };
      case "square":
        return {
          ...baseStyle,
          width: fontSize ? `${fontSize * 0.25}px` : "0.25em",
          height: fontSize ? `${fontSize * 0.25}px` : "0.25em",
          display: "inline-block",
          position: "relative",
          verticalAlign: "text-bottom",
        };
      case "line":
      default:
        return {
          ...baseStyle,
          width: fontSize ? `${fontSize * 0.08}px` : "0.08em",
          height: "1em", // 폰트 사이즈와 동일한 높이
          display: "inline-block",
          verticalAlign: "middle",
          marginBottom: "0.1em", // 약간의 위치 조정으로 중앙에 더 가깝게 정렬
        };
    }
  };

  // 줄바꿈이 있는 텍스트를 처리하기 위한 방법
  const formattedText = () => {
    // 텍스트가 없는 경우에도 커서는 표시
    if (!currentText) {
      return (
        <Box
          component="span"
          sx={{
            ...getCursorStyle(),
            display: "inline-block",
            position: "relative",
            verticalAlign: cursorType === "line" ? "middle" : "text-bottom",
            marginLeft: "0.1em",
            ...(cursorType === "line"
              ? {
                  marginBottom: "0.1em",
                }
              : cursorType === "circle" || cursorType === "square"
              ? {
                  bottom: fontSize ? `${fontSize * 0.2}px` : "0.2em",
                }
              : {}),
            // 깜빡임 애니메이션 적용
            "@keyframes blink": {
              "0%": { opacity: 1 },
              "50%": { opacity: 0 },
              "100%": { opacity: 1 },
            },
          }}
        />
      );
    }

    const lines = currentText.split("\n");

    return (
      <>
        {lines.map((line, i) => (
          <React.Fragment key={i}>
            {line}
            {i === lines.length - 1 ? (
              <Box
                component="span"
                sx={{
                  ...getCursorStyle(),
                  display: "inline-block",
                  position: "relative",
                  verticalAlign:
                    cursorType === "line" ? "middle" : "text-bottom",
                  marginLeft: "0.1em",
                  ...(cursorType === "line"
                    ? {
                        marginBottom: "0.1em",
                      }
                    : cursorType === "circle" || cursorType === "square"
                    ? {
                        bottom: fontSize ? `${fontSize * 0.2}px` : "0.2em",
                      }
                    : {}),
                  // 깜빡임 애니메이션 적용
                  "@keyframes blink": {
                    "0%": { opacity: 1 },
                    "50%": { opacity: 0 },
                    "100%": { opacity: 1 },
                  },
                }}
              />
            ) : (
              <br />
            )}
          </React.Fragment>
        ))}
      </>
    );
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        ...sx,
        // 전역 애니메이션 키프레임 정의
        "@keyframes blink": {
          "0%": { opacity: 1 },
          "50%": { opacity: 0 },
          "100%": { opacity: 1 },
        },
      }}
    >
      <Typography
        ref={textRef}
        variant={currentVariant}
        color={textColor}
        sx={{
          fontWeight: fontWeight,
          fontFamily: fontFamily,
          letterSpacing: "0rem",
          minHeight: "1.2em",
          textAlign: textAlign,
          whiteSpace: "pre-wrap",
          display: "block",
          width: "100%",
          wordBreak: "keep-all",
          ...sx,
        }}
      >
        {formattedText()}
      </Typography>
    </Box>
  );
}

export default TypingEffect;
